<template>
  <div class="container">
    <h1 id="page-title">{{ adminText('organizationAll.ticket_types_326d61d8', 'Ticket Types') }}</h1>
    <p class="page-description">{{ adminText('organizationAll.add_and_manage_type_of_tickets_1226e331', 'Add and manage type of tickets.') }}</p>
    <div class="filters-row" :class="{ 'filters-row--mobile': isMobile }">
      <v-text-field
        v-model="search"
        class="search-field"
        :placeholder="adminText('organizationAll.search_ticket_types_349e709d', 'Search ticket types')"
        outline
        single-line
        hide-details
        append-icon="mdi-magnify"
        @input="handleSearch"
      />
      <v-checkbox
        v-model="includeArchived"
        hide-details
        class="archived-checkbox"
        :label="adminText('organizationAll.show_archived_5084eea9', 'Show archived')"
        @change="handleIncludeArchivedChange($event)"
      />
      <main-button class="create-button" @click="openCreateModal = !openCreateModal">
        <v-icon left color="white">mdi-plus</v-icon>{{ adminText('organizationAll.create_new_ticket_type_5172bbf4', 'Create new ticket type') }}
      </main-button>
    </div>
    <table-list-skeleton v-if="isTableLoading" class="attendee_table" :visible-fields="visibleFields" />
    <div v-else class="attendee_table">
      <div
        v-if="ticketTypes.length === 0"
        class="no-attendees-message-container border__left border__right"
      >
        <span class="no-attendees-message">{{ adminText('organizationAll.no_matching_records_found_0c7f0a64', 'No matching records found.') }}</span>
      </div>
      <table-list
        :hide-checkbox="true"
        :selected.sync="selected"
        :organization-slug="organizationSlug"
        :attendees="localizedTicketTypesTableData"
        body-style="grid-auto-columns: 50px minmax(30px, 250px) !important;"
        :visible-fields="visibleFields"
        :attendee-fields="{}"
        :pagination="pagination"
        @click="handleOpenUpdateModal"
        @sort="handleSort($event)"
      />
      <table-pagination
        :pagination="pagination"
        :total-attendees="totalTicketTypeCount"
        :per-page-options="perPageOptions"
        @change="handlePagination($event)"
        @changePerPage="handlePagination($event)"
      />
    </div>
    <ticket-type-create-modal
      v-if="openCreateModal"
      :organization-venues="organizationVenues"
      @close="openCreateModal = !openCreateModal"
      @closeModal="openCreateModal = !openCreateModal"
      @createTicket="handleCreateTicketType"
    />
    <ticket-type-update-modal
      v-if="openUpdateModal"
      :organization-venues="organizationVenues"
      :initial-values="selectedTicket"
      :create-action="false"
      @closeModal="openUpdateModal = !openUpdateModal"
      @close="openUpdateModal = !openUpdateModal"
      @deleteTicketType="handleDeleteTicketType"
      @archiveTicketType="handleArchiveTicketType"
      @unarchiveTicketType="handleUnarchiveTicketType"
      @updateTicketInfo="handleUpdateTicketType"
    />
    <simple-snackbar
      :open.sync="snackbar.open"
      :message-prop="organizationText(snackbar.message)"
      :color="snackbar.color"
    />
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import tableList from '../../accreditations/attendee-table/list.vue'
import TicketTypeCreateModal from '@/components/organizations/ticket-types/TicketTypeCreateModal.vue'
import TicketTypeUpdateModal from '@/components/organizations/ticket-types/TicketTypeUpdateModal.vue'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import tablePagination from '../../accreditations/attendee-table/pagination.vue'
import TableListSkeleton from '../../accreditations/attendee-table/list-skeleton.vue'
import {
  GET_ORGANIZATION_ORDER_ITEM_TEMPLATES_AND_VENUES_AND_SEARCH,
  GET_ORGANIZATION_VENUES_FOR_TICKET_TYPES,
  CREATE_ORGANIZATION_ORDER_ITEM_TEMPLATES,
  DELETE_ORGANIZATION_ORDER_ITEM_TEMPLATES,
  UPDATE_ORGANIZATION_ORDER_ITEM_TEMPLATES,
  ARCHIVE_ORGANIZATION_ORDER_ITEM_TEMPLATE,
  UNARCHIVE_ORGANIZATION_ORDER_ITEM_TEMPLATE,
} from '@/graphql/orderItemTemplate.graphql'

const DEFAULT_PER_PAGE = 10
const MAX_PER_PAGE = 100
const PER_PAGE_OPTIONS = [5, DEFAULT_PER_PAGE, 25, MAX_PER_PAGE]
const SEARCH_DEBOUNCE_MS = 300

export default {
  mixins: [OrganizationLocalization],
  name: 'ticket-type',
  components: {
    tableList,
    TicketTypeCreateModal,
    SimpleSnackbar,
    TicketTypeUpdateModal,
    tablePagination,
    TableListSkeleton,
  },
  props: {
    organizationSlug: {
      type: String,
    },
  },
  data() {
    const { pagination, search, includeArchived } = this.getInitialFromQueryParams()

    return {
      ticketTypes: [],
      ticketTypesTableData: [],
      pagination,
      search,
      includeArchived,
      selected: [],
      selectedTicket: {},
      snackbar: {
        open: false,
        message: '',
        color: 'primary',
      },
      openCreateModal: false,
      openUpdateModal: false,
      organizerId: null,
      organizationVenues: [],
      visibleFields: ['Ticket type', 'Odeum', 'Price'],
      totalTicketTypeCount: 0,
      paginationLoading: false,
      perPageOptions: PER_PAGE_OPTIONS,
      searchTimeout: null,
    }
  },
  computed: {
    localizedTicketTypesTableData() {
      return (this.ticketTypesTableData || []).map(row => {
        const original = this.ticketTypes.find(ticket => ticket.id === row.id)

        return original && original.archivedAt
          ? { ...row, 'ticket type': this.organizationArchivedName(original.name) } : row
      })
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown
    },
    isTableLoading() {
      return this.$apollo.loading || this.paginationLoading
    },
  },
  async mounted() {
    await Promise.all([this.fetchOrganizationVenues(), this.fetchTicketTypes()])
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },
  methods: {
    handleSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      this.searchTimeout = setTimeout(() => {
        this.handleSearchDebounced()
      }, SEARCH_DEBOUNCE_MS)
    },
    async handleSearchDebounced() {
      this.pagination = {
        ...this.pagination,
        page: 1,
      }

      await this.replaceRouteQuery({
        ...this.$route.query,
        search: this.search || undefined,
        page: 1,
      })
      await this.fetchTicketTypes()
    },
    async handleIncludeArchivedChange(value) {
      this.includeArchived = !!value
      this.pagination = {
        ...this.pagination,
        page: 1,
      }

      await this.replaceRouteQuery({
        ...this.$route.query,
        includeArchived: this.includeArchived || undefined,
        page: 1,
      })
      await this.fetchTicketTypes()
    },
    handleOpenUpdateModal(id) {
      this.selectedTicket = this.ticketTypes.find(ticket => ticket.id === id)
      this.openUpdateModal = !this.openUpdateModal
    },
    async handleCreateTicketType(ticketInfo) {
      this.openCreateModal = !this.openCreateModal
      const orderItemTemplateInput = this.setTicketVenueSections(ticketInfo)
      const { errors } = await this.$apollo.mutate({
        mutation: CREATE_ORGANIZATION_ORDER_ITEM_TEMPLATES,
        variables: {
          organizationSlug: this.organizationSlug,
          orderItemTemplateInput,
        },
      })
      await this.showUserMessage(errors, 'created')
    },
    async handleUpdateTicketType(ticketInfo) {
      this.openUpdateModal = !this.openUpdateModal
      const orderItemTemplateInput = this.prepareTicketTypeInput(ticketInfo)
      // create-only flag, the backend rejects/ignores it on update
      delete orderItemTemplateInput.isAccessible

      const { errors } = await this.$apollo.mutate({
        mutation: UPDATE_ORGANIZATION_ORDER_ITEM_TEMPLATES,
        variables: {
          organizationSlug: this.organizationSlug,
          orderItemTemplateInput,
        },
      })
      await this.showUserMessage(errors, 'updated')
    },
    async handleDeleteTicketType(orderItemTemplateId) {
      this.openUpdateModal = !this.openUpdateModal
      const { errors } = await this.$apollo.mutate({
        mutation: DELETE_ORGANIZATION_ORDER_ITEM_TEMPLATES,
        variables: {
          organizationSlug: this.organizationSlug,
          orderItemTemplateId,
        },
      })
      await this.showUserMessage(errors, 'deleted')
    },
    async handleArchiveTicketType(orderItemTemplateId) {
      this.openUpdateModal = !this.openUpdateModal
      const { errors } = await this.$apollo.mutate({
        mutation: ARCHIVE_ORGANIZATION_ORDER_ITEM_TEMPLATE,
        variables: {
          organizationSlug: this.organizationSlug,
          orderItemTemplateId,
        },
      })
      await this.showUserMessage(errors, 'archived')
    },
    async handleUnarchiveTicketType(orderItemTemplateId) {
      this.openUpdateModal = !this.openUpdateModal
      const { errors } = await this.$apollo.mutate({
        mutation: UNARCHIVE_ORGANIZATION_ORDER_ITEM_TEMPLATE,
        variables: {
          organizationSlug: this.organizationSlug,
          orderItemTemplateId,
        },
      })
      await this.showUserMessage(errors, 'unarchived')
    },
    async showUserMessage(errors, msg) {
      if (errors) {
        if (errors.length && errors[0].message) {
          this.snackbar = {
            open: true,
            message: errors[0].message,
            color: 'error',
          }
        } else {
          this.snackbar = {
            open: true,
            message: 'Oops, something went wrong!',
            color: 'error',
          }
        }
      } else {
        this.snackbar = {
          open: true,
          message: this.organizationMessage('organizationAll.successfully_value0_ticket_type_0824c220', 'Successfully {{value0}} ticket type!', { value0: msg }),
        }

        this.selected = []
        await this.fetchTicketTypes()
      }
    },
    setTicketVenueSections(ticketInfo) {
      const venueSection = ticketInfo.venueSection || []
      if (
        ticketInfo.venueId &&
        (!venueSection.length || venueSection.includes('All'))
      ) {
        const selectedVenue = this.organizationVenues.find(venue => venue.id == ticketInfo.venueId)
        if (selectedVenue && selectedVenue.predefinedSections) {
          ticketInfo.venueSection = Object.keys(selectedVenue.predefinedSections)
        }
      }

      return ticketInfo
    },
    prepareTicketTypeInput(ticketInfo) {
      const orderItemTemplateInput = { ...this.setTicketVenueSections({ ...ticketInfo }) }
      const ignoredFields = [
        'venue',
        'currency',
        'createdAt',
        'orderItems',
        'hasSoldOrderItems',
        'archivedAt',
        '__typename',
      ]

      ignoredFields.forEach(field => {
        delete orderItemTemplateInput[field]
      })

      return orderItemTemplateInput
    },
    async handlePagination({ page, per_page }) {
      const { page: currentPage, per_page: currentPerPage } = this.pagination
      const perPage = this.normalizePerPage(per_page)
      const normalizedCurrentPerPage = this.normalizePerPage(currentPerPage)
      const nextPage = perPage !== normalizedCurrentPerPage ? 1 : page

      if (nextPage < 1) return
      if (
        nextPage > currentPage &&
        !(this.totalTicketTypeCount - normalizedCurrentPerPage * currentPage > 0)
      )
        return

      this.pagination = {
        ...this.pagination,
        per_page: perPage,
        page: nextPage,
      }

      await this.addPaginationToRoute(this.pagination)
      await this.fetchTicketTypes()
    },
    async handleSort(sortBy) {
      const { per_page, page, sortBy: thisSortBy, descending } = this.pagination
      const pagination = {
        page,
        per_page,
        sortBy,
        descending: sortBy === thisSortBy ? !descending : false,
      }
      await this.addPaginationToRoute(pagination)
      this.pagination = pagination
      await this.fetchTicketTypes()
    },
    getInitialFromQueryParams() {
      const { per_page, page, sortBy, descending, search, includeArchived } = this.$route.query

      return {
        pagination: {
          page: parseInt(page, 10) || 1,
          per_page: this.normalizePerPage(per_page),
          sortBy: sortBy || 'createdAt',
          descending: this.parseDescending(descending, true),
        },
        search: search || '',
        includeArchived: includeArchived === true || includeArchived === 'true',
      }
    },
    async addPaginationToRoute({ page, per_page, sortBy, descending }) {
      await this.replaceRouteQuery({
        ...this.$route.query,
        per_page,
        page,
        sortBy,
        descending,
      })
    },
    async replaceRouteQuery(query) {
      try {
        await this.$router.replace({
          query,
        })
      } catch (error) {
        if (!error || error.name !== 'NavigationDuplicated') {
          throw error
        }
      }
    },
    normalizePerPage(perPage) {
      const parsedPerPage = parseInt(perPage, 10)
      if (!Number.isFinite(parsedPerPage) || parsedPerPage < 1) {
        return DEFAULT_PER_PAGE
      }

      return Math.min(parsedPerPage, MAX_PER_PAGE)
    },
    parseDescending(descending, defaultValue = false) {
      if (descending === undefined) {
        return defaultValue
      }

      return descending === true || descending === 'true' || descending === '1'
    },
    async fetchOrganizationVenues() {
      const {
        data: { getOrganizationVenues },
        errors,
      } = await this.$apollo.query({
        query: GET_ORGANIZATION_VENUES_FOR_TICKET_TYPES,
        variables: {
          organizationSlug: this.organizationSlug,
        },
      })

      if (errors) {
        await this.showUserMessage(errors)

        return
      }

      this.organizationVenues = getOrganizationVenues.organizationVenues.map(organizationVenue => {
        return {
          ...organizationVenue.venue,
          id: parseInt(organizationVenue.venue.id, 10),
        }
      })
    },
    async fetchTicketTypes() {
      this.paginationLoading = true
      try {
        const {
          data: { getOrganizationOrderItemTemplatesAndSearch },
          errors,
        } = await this.$apollo.query({
          query: GET_ORGANIZATION_ORDER_ITEM_TEMPLATES_AND_VENUES_AND_SEARCH,
          variables: {
            organizationSlug: this.organizationSlug,
            pagination: this.pagination,
            searchTerm: this.search || undefined,
            includeArchived: this.includeArchived,
          },
          fetchPolicy: 'network-only',
        })

        if (errors) {
          await this.showUserMessage(errors)

          return
        }

        this.totalTicketTypeCount = getOrganizationOrderItemTemplatesAndSearch.count
        this.ticketTypes = getOrganizationOrderItemTemplatesAndSearch.rows

        this.ticketTypesTableData =
          this.ticketTypes.map(ticket => {
            const venueName = ticket.venue ? ticket.venue.name : ''

            return {
              id: ticket.id,
              'ticket type': ticket.archivedAt ? `${ticket.name} (archived)` : ticket.name,
              odeum: venueName,
              price: ticket.price,
              currency: ticket.currency,
            }
          }) || {}
      } finally {
        this.paginationLoading = false
      }
    },
  },
}
</script>

<style scoped>
.page-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  color: #979797;
}
.filters-row {
  display: flex;
  align-items: center;
  column-gap: 1rem;
  margin-bottom: 1.5rem;
}
.search-field {
  max-width: 385px;
  margin-top: 0;
  padding-top: 0;
}
.search-field >>> .v-input__slot {
  border: 1px solid #d9d9d9 !important;
  border-radius: 4px;
  background: #ffffff !important;
  margin-bottom: 0;
}
.archived-checkbox {
  margin-top: 0;
  flex: none;
}
.create-button {
  height: 48px;
  margin-left: auto !important;
  margin-right: 0;
}
.filters-row--mobile {
  flex-direction: column;
  align-items: stretch;
  row-gap: 0.75rem;
}
.filters-row--mobile .search-field {
  max-width: 100%;
}
.filters-row--mobile .create-button {
  margin-left: 0 !important;
}
.container {
  padding: 0 !important;
  margin: 0 !important;
  display: flex;
  flex-direction: column;
  max-width: 100%;
}
#page-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}
</style>
